/* eslint-disable */

import { Promise as promisePolyfill } from 'core-js/actual/promise';
import { Symbol as symbolPolyfill } from 'core-js/actual/symbol';
import { URL as urlPolyfill } from 'core-js/actual/url';
import { WeakSet as weakSetPolyfill } from 'core-js/actual/weak-set';
import { fetch as fetchPolyfill } from 'whatwg-fetch';
import { TrueType } from './modules/enums/TrueType';

/**
 * @copyright © Paramount Streaming
 * @author Stan Gletski on 2019-03-15.
 * @description Library of Polyfills
 */

/**
 * @description INFO: Array.includes polyfill
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/includes }
 */
export let arrayIncludes = () => {
	// https://tc39.github.io/ecma262/#sec-array.prototype.includes
	if ( !Array.prototype.includes ) {
		Object.defineProperty( Array.prototype, 'includes', {
			value: function ( valueToFind, fromIndex ) {

				if ( this == null ) {
					throw new TypeError( '"this" is null or not defined' );
				}

				// 1. Let O be ? ToObject(this value).
				var o = Object( this );

				// 2. Let len be ? ToLength(? Get(O, "length")).
				var len = o.length >>> 0;

				// 3. If len is 0, return false.
				if ( len === 0 ) {
					return false;
				}

				// 4. Let n be ? ToInteger(fromIndex).
				//    (If fromIndex is undefined, this step produces the value 0.)
				var n = fromIndex | 0;

				// 5. If n ≥ 0, then
				//  a. Let k be n.
				// 6. Else n < 0,
				//  a. Let k be len + n.
				//  b. If k < 0, let k be 0.
				var k = Math.max( n >= 0 ? n : len - Math.abs( n ), 0 );

				function sameValueZero( x, y ) {
					return x === y || ( typeof x === TrueType.NUMBER && typeof y === TrueType.NUMBER && isNaN( x ) && isNaN( y ) );
				}

				// 7. Repeat, while k < len
				while ( k < len ) {
					// a. Let elementK be the result of ? Get(O, ! ToString(k)).
					// b. If SameValueZero(valueToFind, elementK) is true, return true.
					if ( sameValueZero( o[ k ], valueToFind ) ) {
						return true;
					}
					// c. Increase k by 1.
					k++;
				}

				// 8. Return false
				return false;
			}
		} );
	}
};

/**
 * @description INFO: Object.keys polyfill
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/keys#Polyfill }
 */
export let objectKeys = () => {
	if ( !Object.keys ) {
		Object.keys = ( function () {
			'use strict';
			var hasOwnProperty = Object.prototype.hasOwnProperty,
				hasDontEnumBug = !( { toString: null } ).propertyIsEnumerable( 'toString' ),
				dontEnums = [
					'toString',
					'toLocaleString',
					'valueOf',
					'hasOwnProperty',
					'isPrototypeOf',
					'propertyIsEnumerable',
					'constructor'
				],
				dontEnumsLength = dontEnums.length;

			return function ( obj ) {
				if ( typeof obj !== TrueType.FUNCTION && ( typeof obj !== TrueType.OBJECT || obj === null ) ) {
					throw new TypeError( 'Object.keys called on non-object' );
				}

				var result = [], prop, i;

				for ( prop in obj ) {
					if ( hasOwnProperty.call( obj, prop ) ) {
						result.push( prop );
					}
				}

				if ( hasDontEnumBug ) {
					for ( i = 0; i < dontEnumsLength; i++ ) {
						if ( hasOwnProperty.call( obj, dontEnums[ i ] ) ) {
							result.push( dontEnums[ i ] );
						}
					}
				}
				return result;
			};
		}() );
	}
};

/**
 * @description INFO: Object.assign polyfill
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/assign }
 */
export let objectAssign = () => {
	if ( typeof Object?.assign != TrueType.FUNCTION ) {
		// Must be writable: true, enumerable: false, configurable: true
		Object.defineProperty(
			Object,
			'assign',
			{
				value: function assign( target, varArgs ) { // .length of function is 2
					'use strict';
					if ( target == null ) { // TypeError if undefined or null
						throw new TypeError( 'Cannot convert undefined or null to object' );
					}

					let to = Object( target );

					for ( let index = 1; index < arguments.length; index++ ) {
						let nextSource = arguments[ index ];

						if ( nextSource != null ) { // Skip over if undefined or null
							for ( let nextKey in nextSource ) {
								// Avoid bugs when hasOwnProperty is shadowed
								if ( Object.prototype.hasOwnProperty.call( nextSource, nextKey ) ) {
									to[ nextKey ] = nextSource[ nextKey ];
								}
							}
						}
					}
					return to;
				},
				writable: true,
				configurable: true
			}
		);
	}
};

/**
 * @description Array.prototype.forEach() polyfill
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/forEach }
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/NodeList/forEach }
 */
export let arrayForEach = () => {
	// Production steps of ECMA-262, Edition 5, 15.4.4.18
	// Reference: http://es5.github.io/#x15.4.4.18
	if ( !Array.prototype.forEach ) {

		Array.prototype.forEach = function ( callback/*, thisArg*/ ) {

			var T, k;

			if ( this == null ) {
				throw new TypeError( 'this is null or not defined' );
			}

			// 1. Let O be the result of calling toObject() passing the
			// |this| value as the argument.
			var O = Object( this );

			// 2. Let lenValue be the result of calling the Get() internal
			// method of O with the argument "length".
			// 3. Let len be toUint32(lenValue).
			var len = O.length >>> 0;

			// 4. If isCallable(callback) is false, throw a TypeError exception.
			// See: http://es5.github.com/#x9.11
			if ( typeof callback !== TrueType.FUNCTION ) {
				throw new TypeError( callback + ' is not a function' );
			}

			// 5. If thisArg was supplied, let T be thisArg; else let
			// T be undefined.
			if ( arguments.length > 1 ) {
				T = arguments[ 1 ];
			}

			// 6. Let k be 0.
			k = 0;

			// 7. Repeat while k < len.
			while ( k < len ) {

				var kValue;

				// a. Let Pk be ToString(k).
				//    This is implicit for LHS operands of the in operator.
				// b. Let kPresent be the result of calling the HasProperty
				//    internal method of O with argument Pk.
				//    This step can be combined with c.
				// c. If kPresent is true, then
				if ( k in O ) {

					// i. Let kValue be the result of calling the Get internal
					// method of O with argument Pk.
					kValue = O[ k ];

					// ii. Call the Call internal method of callback with T as
					// the this value and argument list containing kValue, k, and O.
					callback.call( T, kValue, k, O );
				}
				// d. Increase k by 1.
				k++;
			}
			// 8. return undefined.
		};
	}

	if ( window.NodeList && !NodeList.prototype.forEach ) {
		//@ts-ignore
		NodeList.prototype.forEach = Array.prototype.forEach;
	}
};

/**
 * @description Array.prototype.at() polyfill
 * @see {@link https://github.com/tc39/proposal-relative-indexing-method#polyfill}
 */
export const arrayAt = (): void => {
	if ( ![].at ) {
		Array.prototype.at = function ( n ) {
			n = Math.trunc( n ) || 0;
			if ( n < 0 ) n += this.length;
			if ( n < 0 || n >= this.length ) return undefined;
			return this[ n ];
		}
	}
}

/**
 * @description INFO: String.repeat polyfill
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/repeat#Polyfill}
 */
export let stringRepeat = () => {
	if ( !String.prototype.repeat ) {
		String.prototype.repeat = function ( count ) {
			'use strict';
			if ( this == null )
				throw new TypeError( 'can\'t convert ' + this + ' to object' );

			var str = '' + this;
			// To convert string to integer.
			count = +count;
			// Check NaN
			if ( count != count )
				count = 0;

			if ( count < 0 )
				throw new RangeError( 'repeat count must be non-negative' );

			if ( count == Infinity )
				throw new RangeError( 'repeat count must be less than infinity' );

			count = Math.floor( count );
			if ( str.length == 0 || count == 0 )
				return '';

			// Ensuring count is a 31-bit integer allows us to heavily optimize the
			// main part. But anyway, most current (August 2014) browsers can't handle
			// strings 1 << 28 chars or longer, so:
			if ( str.length * count >= 1 << 28 )
				throw new RangeError( 'repeat count must not overflow maximum string size' );

			var maxCount = str.length * count;
			count = Math.floor( Math.log( count ) / Math.log( 2 ) );
			while ( count ) {
				str += str;
				count--;
			}
			str += str.substring( 0, maxCount - str.length );
			return str;
		}
	}
}

/**
 * @description String.endsWith() polyfill
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/endsWith#Polyfill}
 */
export let stringEndsWith = () => {
	if ( !String.prototype.endsWith ) {
		String.prototype.endsWith = function ( search, this_len ) {
			if ( this_len === undefined || this_len > this.length ) {
				this_len = this.length;
			}
			return this.substring( this_len - search.length, this_len ) === search;
		};
	}
}

/**
 * @description INFO: String.trim polyfill
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/Trim#Polyfill}
 */
export let stringTrim = () => {
	if ( !String.prototype.trim ) {
		String.prototype.trim = function () {
			return this.replace( /^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g, '' );
		};
	}
};

/**
 * @description Polyfill for string.replaceAll()
 * @see {@link https://gomakethings.com/how-to-write-your-own-vanilla-js-polyfill/}
 */
export const stringReplaceAll = (): void => {
	if ( !String.prototype.replaceAll ) {
		String.prototype.replaceAll = function ( str, newStr ) {

			// If a regex pattern
			if ( Object.prototype.toString.call( str ).toLowerCase() === '[object regexp]' ) {
				return this.replace( str, newStr );
			}

			// If a string
			return this.replace( new RegExp( str, 'g' ), newStr );

		};
	}
}

/**
 * @description INFO: Element prepend polyfill: missing on IE11, Edge
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/ParentNode/prepend#Polyfill}
 */
export let elementPrepend = (): void => {
	( function ( arr ) {
		arr.forEach( function ( item ) {
			if ( item.hasOwnProperty( 'prepend' ) ) {
				return;
			}
			Object.defineProperty( item, 'prepend', {
				configurable: true,
				enumerable: true,
				writable: true,
				value: function prepend() {
					var argArr = Array.prototype.slice.call( arguments ),
						docFrag = document.createDocumentFragment();

					argArr.forEach( function ( argItem ) {
						var isNode = argItem instanceof Node;
						docFrag.appendChild( isNode ? argItem : document.createTextNode( String( argItem ) ) );
					} );

					this.insertBefore( docFrag, this.firstChild );
				}
			} );
		} );
	} )( [ Element.prototype, Document.prototype, DocumentFragment.prototype ] );
};

/**
 * @description INFO: Element append polyfill: missing on IE11, Edge
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/ParentNode/append#Polyfill}
 */
export let elementAppend = (): void => {
	( function ( arr ) {
		arr.forEach( function ( item ) {
			if ( item.hasOwnProperty( 'append' ) ) {
				return;
			}
			Object.defineProperty( item, 'append', {
				configurable: true,
				enumerable: true,
				writable: true,
				value: function append() {
					var argArr = Array.prototype.slice.call( arguments ),
						docFrag = document.createDocumentFragment();

					argArr.forEach( function ( argItem ) {
						var isNode = argItem instanceof Node;
						docFrag.appendChild( isNode ? argItem : document.createTextNode( String( argItem ) ) );
					} );

					this.appendChild( docFrag );
				}
			} );
		} );
	} )( [ Element.prototype, Document.prototype, DocumentFragment.prototype ] );
}

/**
 * @description replaceChildren polyfill
 * @see {@link https://github.com/yuzhe-han/ParentNode-replaceChildren}
 */
export let replaceChildren = (): void => {
	( function () {
		// polyfill for replaceChildren
		if ( Node.prototype[ 'replaceChildren' ] === undefined ) {
			Node.prototype[ 'replaceChildren' ] = function ( addNodes ) {
				while ( this.lastChild ) {
					this.removeChild( this.lastChild );
				}
				if ( addNodes !== undefined ) {
					this.append( addNodes );
				}
			};
		}
	}() );
}

/**
 * @description PLBCK-5070: Object.fromEntries polyfill for Samsung Tizen (Chrome 63)
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/fromEntries#Polyfill}
 */
export const objectFromEntries = (): void => {
	if ( !Object.fromEntries ) {
		Object.fromEntries = function<T = unknown>( entries: Iterable<readonly [PropertyKey, T]> ): { [k: string]: T } {
			const result: { [k: string]: T } = {};
			for ( const [ key, value ] of entries ) {
				result[ key as string ] = value;
			}
			return result;
		};
	}
};

/**
 * @description Polyfill for Object.getOwnPropertyDescriptors
 * @see {@link https://www.proposals.es/proposals/Object.getOwnPropertyDescriptors}
 */
export let getOwnPropertyDescriptors = (): void => {
	if ( !Object.hasOwnProperty( 'getOwnPropertyDescriptors' ) ) {
		Object.defineProperty(
			Object,
			'getOwnPropertyDescriptors',
			{
				configurable: true,
				writable: true,
				value: function getOwnPropertyDescriptors( object ) {
					return Reflect.ownKeys( object ).reduce( ( descriptors, key ) => {
						return Object.defineProperty(
							descriptors,
							key,
							{
								configurable: true,
								enumerable: true,
								writable: true,
								value: Object.getOwnPropertyDescriptor( object, key )
							}
						);
					}, {} );
				}
			}
		);
	}
}

/**
 * @description Polyfill WeakSet with core-js
 */
export let weakSet = (): void => {
	// Make sure WeakSet polyfill is available on window object
	if ( !window?.WeakSet ) {
		window.WeakSet = weakSetPolyfill;
	}
}

/**
 * @description Polyfill Promise with core-js
 */
export const polyfillPromise = (): void => {
	// Make sure Promise polyfill is available on window object
	if ( !window?.Promise ) {
		window.Promise = promisePolyfill;
	}
}

/**
 * @description Polyfill Symbol with core-js
 */
export const polyfillSymbol = (): void => {
	// Make sure Symbol polyfill is available on window object
	if ( !window?.Symbol ) {
		window.Symbol = symbolPolyfill;
	}
}

/**
 * @description Polyfill fetch with whatwg-fetch
 */
export const polyfillFetch = (): void => {
	// Make sure fetch polyfill is available on window object
	if ( !window?.fetch ) {
		window.fetch = fetchPolyfill;
	}
}

/**
 * @description Polyfill URL with core-js
 */
export const polyfillURL = (): void => {
	if ( !window?.URL ) {
		window.URL = urlPolyfill;
	}
}

/**
 * @description Add all Polyfills
 */
let all = (): void => {
	arrayAt();
	arrayForEach();
	arrayIncludes();
	elementAppend();
	elementPrepend();
	getOwnPropertyDescriptors();
	objectAssign();
	objectFromEntries();
	objectKeys();
	polyfillFetch();
	polyfillPromise();
	polyfillSymbol();
	polyfillURL();
	replaceChildren();
	stringEndsWith();
	stringRepeat();
	stringReplaceAll();
	stringTrim();
	weakSet();
};

/**
 * @description DB of Polyfills
 */
export let Polyfill = {
	arrayIncludes: arrayIncludes,
	arrayAt: arrayAt,
	objectKeys: objectKeys,
	objectAssign: objectAssign,
	objectFromEntries: objectFromEntries,
	arrayForEach: arrayForEach,
	stringTrim: stringTrim,
	stringRepeat: stringRepeat,
	stringReplaceAll: stringReplaceAll,
	stringEndsWith: stringEndsWith,
	elementPrepend: elementPrepend,
	elementAppend: elementAppend,
	replaceChildren: replaceChildren,
	getOwnPropertyDescriptors: getOwnPropertyDescriptors,
	weakSet: weakSet,
	polyfillPromise: polyfillPromise,
	polyfillFetch: polyfillFetch,
	polyfillURL: polyfillURL,
	all: all
};

export const initializePolyfills = (): void => {
	arrayAt();
	arrayForEach();
	getOwnPropertyDescriptors();
	objectFromEntries();
	polyfillFetch();
	polyfillPromise();
	polyfillSymbol();
	polyfillURL();
	replaceChildren();
	stringReplaceAll();
	weakSet();
}